import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Columns3, Layers } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { GroupPicker, StatusPicker, useProjectStatuses } from '@/features/tasks/public';
import type { ConvertToTaskFormValues } from '../schemas';
import type { NoteTaskProposal } from '../types';
import type { NameMatch } from '../utils/matchOptionByName';
import { assigneeHint, nameHint } from '../utils/convertToTaskHints';
import { ConvertToTaskField } from './ConvertToTaskField';
import { ConvertToTaskPickerTrigger } from './ConvertToTaskPickerTrigger';
import { ConvertToTaskSelect } from './ConvertToTaskSelect';

const AI_FILLED = 'border-primary';
const FIELD_ICON = 'size-4 shrink-0 text-muted-foreground';

interface ConvertToTaskAssignmentSectionProps {
  control: Control<ConvertToTaskFormValues>;
  isPending: boolean;
  proposal: NoteTaskProposal | null;
  projectId: string;
  boardId: string;
  memberOptions: { value: string; label: string; icon: React.ReactNode }[];
  assigneeMatch: NameMatch;
  orgMembers: { displayName: string }[];
  boardOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  statusMatch: NameMatch;
  groupOptions: { value: string; label: string }[];
  groupMatch: NameMatch;
  proposedStatus: string | null;
  proposedGroup: string | null;
  proposedAssignee: string | null;
  priorityOptions: { value: string; label: string; icon: React.ReactNode }[];
}

/** Who the task is for, where it lives on the board, and how urgent it is. */
export function ConvertToTaskAssignmentSection({
  control,
  isPending,
  proposal,
  projectId,
  boardId,
  memberOptions,
  assigneeMatch,
  orgMembers,
  boardOptions,
  statusOptions,
  statusMatch,
  groupOptions,
  groupMatch,
  proposedStatus,
  proposedGroup,
  proposedAssignee,
  priorityOptions,
}: ConvertToTaskAssignmentSectionProps) {
  const { t } = useTranslation('notes');
  const aiSuggested = t('convertToTask.aiSuggested');
  const aiShort = t('convertToTask.aiShort');
  const { statuses: projectStatuses } = useProjectStatuses(boardId);

  return (
    <>
      <ConvertToTaskSelect
        control={control}
        name="assigneeId"
        label={t('convertToTask.fields.assignee')}
        aiLabel={proposal?.assigneeName ? aiSuggested : undefined}
        options={memberOptions}
        placeholder={t('convertToTask.fields.assigneePlaceholder')}
        disabled={isPending || !projectId}
        highlighted={!!proposedAssignee}
        hint={assigneeHint({
          t,
          proposal,
          projectId,
          memberOptionsCount: memberOptions.length,
          assigneeMatch,
          orgMembers,
        })}
        data-testid="notes-convert-assignee-select"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <ConvertToTaskSelect
          control={control}
          name="boardId"
          label={t('convertToTask.fields.board')}
          required
          options={boardOptions}
          placeholder={t('convertToTask.fields.boardPlaceholder')}
          icon={<Columns3 className={FIELD_ICON} aria-hidden />}
          disabled={isPending || !projectId}
          data-testid="notes-convert-board-select"
        />
        {/* The task detail's own picker — it creates and edits statuses inline, so a
            note naming a status the board lacks can be resolved without leaving. */}
        <Controller
          name="statusId"
          control={control}
          render={({ field, fieldState }) => (
            <ConvertToTaskField
              label={t('convertToTask.fields.status')}
              aiLabel={proposal?.statusName ? aiShort : undefined}
              hint={nameHint(
                t,
                statusMatch,
                proposal?.statusName,
                statusOptions.length,
                'convertToTask.observations.statusNotFound',
              )}
              error={fieldState.error}
            >
              <StatusPicker
                boardId={boardId}
                currentStatus={projectStatuses.find((s) => s.id === field.value)}
                onSelect={(statusId) => field.onChange(statusId)}
                disabled={isPending || !boardId}
                trigger={
                  <ConvertToTaskPickerTrigger
                    label={projectStatuses.find((s) => s.id === field.value)?.name}
                    placeholder={t('convertToTask.fields.statusPlaceholder')}
                    highlighted={!!proposedStatus}
                    disabled={isPending || !boardId}
                    data-testid="notes-convert-status-select"
                  />
                }
              />
            </ConvertToTaskField>
          )}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Same picker as the task detail, which can create a group inline.
            It renders its own trigger, so the field box wraps it. */}
        <Controller
          name="groupId"
          control={control}
          render={({ field, fieldState }) => (
            <ConvertToTaskField
              label={t('convertToTask.fields.group')}
              aiLabel={proposal?.groupName ? aiShort : undefined}
              hint={nameHint(
                t,
                groupMatch,
                proposal?.groupName,
                groupOptions.length,
                'convertToTask.observations.groupNotFound',
              )}
              error={fieldState.error}
            >
              <div
                data-testid="notes-convert-group-select"
                className={cn(
                  'flex h-10 items-center gap-2 rounded-md border px-3 shadow-xs',
                  proposedGroup && AI_FILLED,
                  (isPending || !boardId) && 'opacity-50',
                )}
              >
                <Layers className={FIELD_ICON} aria-hidden />
                <GroupPicker
                  boardId={boardId}
                  projectId={projectId}
                  currentGroupId={field.value}
                  onSelect={(groupId) => field.onChange(groupId)}
                  disabled={isPending || !boardId}
                />
              </div>
            </ConvertToTaskField>
          )}
        />
        <ConvertToTaskSelect
          control={control}
          name="priority"
          label={t('convertToTask.fields.priority')}
          aiLabel={proposal?.priority != null ? aiShort : undefined}
          options={priorityOptions}
          placeholder={t('convertToTask.fields.priorityPlaceholder')}
          disabled={isPending}
          highlighted={proposal?.priority != null}
          numeric
          data-testid="notes-convert-priority-select"
        />
      </div>
    </>
  );
}
